/**
 * 給与計算用の勤怠テーブル。行 = worker、列 = 勤怠 DB の月次集計項目。
 *
 * 列の一次ソースは payroll item カタログ（`payrollItemCatalog.ts`）の `category: 'reference'`。
 * 供給元が勤怠データ・勤怠集計の R021–R039 と R050 を全て列として持ち、各列は
 * `payrollItemId` でカタログを名指しする。表示用の `code` は利用者が編集し得るので
 * 永続化せず、コメントでの言及にとどめる。
 *
 * `payrollItemId` は `code` から機械的に導けない。R023 残業時間だけ id が `pi-overtime-hours`
 * （カタログ刷新前からの id を引き継いでいる）で、`pi-r023` は存在しない。
 *
 * 列順は 氏名 / 社員番号 → payroll item を code 昇順 → payroll item に無い細目 の順。
 * カタログとの突合を目で追えるようにするため、読みやすさより code 順を優先している。
 *
 * 末尾の細目（集計対象期間・所定内労働時間・法定休日 / 法定外休日の労働時間・休日出勤日数・
 * 振替休日 / 代休 / 特別休暇の取得日数・固定残業時間の超過分）は payroll item に無いが
 * 給与計算に要る項目で、給与計算入力スナップショット（`payrollInputSnapshots.ts` の
 * `ATTENDANCE_SEEDS`）と割増計算の内訳由来。`sourcePath` の表記もそれに合わせている。
 *
 * カタログ上の時間項目は `unitOfMeasurement: { kind: 'duration', unit: 'minute' }` で分を持つ。
 * この表はその表示換算として「時間:分」表記の文字列で持つ。分に丸めた小数（1.5 時間）だと
 * 60 進の繰り上がりが目で追えず、割増時間として読み違えるため。
 *
 * 値はサンプル（架空の従業員）。メンバー・契約テーブルと同じ 10 名・同じ並び。
 */

import type { PayrollReferenceCatalogTable } from '../../types';

/**
 * 給与計算用の勤怠テーブル。集計対象は 2026 年 7 月度（所定 22 日、週 4 日勤務の
 * EMP-1004 のみ 16 日）。
 *
 * 値の整合で守っていること:
 * - 出勤日数（R025）+ 欠勤日数（R021）+ 有給取得日数（R026）+ 特別休暇取得日数
 *   = 当月所定労働日数（R039）。
 * - 当月所定労働時間（R022）= 契約テーブルの所定労働時間（1日）× 当月所定労働日数（R039）。
 * - 所定内労働時間 = 契約テーブルの所定労働時間（1日）× 出勤日数 − 遅刻早退時間（R029）。
 *   遅刻早退は所定時間の中の不就労なので、時間外側ではなく所定内側から引く。
 * - 時間外の内訳（R032 45h以内 / R033 45h超 / R034 60h超）の合計 = 時間外労働時間
 *   （制度反映 R038）。法定外休日（所定休日）の労働時間は R038 に含め、法定休日の
 *   労働時間は含めない（60 時間超の算定に法定休日労働を入れないため）。
 * - 深夜の内訳（R035 法定内 / R036 法定外 / R037 法定休日）の合計 = 深夜労働時間（R027）。
 *   深夜は所定内・時間外に重なる加算なので、労働時間数（R050）には足し込まない。
 * - 休日労働時間（R028）= 法定休日労働時間 + 法定外休日労働時間。
 * - 労働時間数（R050）= 所定内労働時間 + 時間外労働時間（制度反映 R038）+ 法定休日労働時間。
 *   法定外休日は R038 に含めているので、ここで二重に足さない。
 * - 残業時間（R023）は打刻ベースの所定超過時間、時間外労働時間（R038）は勤務時間制度を
 *   反映した後の法定外時間外。両者が食い違うのは契約テーブルの勤務時間制度区分（R015）が
 *   「変形労働時間制」の行（EMP-1006 / EMP-1008）だけで、変形の枠が吸収した分だけ
 *   R038 が小さくなる。制度が「通常の労働時間制」の行では必ず同値になる。
 * - みなし残業時間（R024）= 契約テーブルの固定残業時間。定めが無い行は勤怠集計としては
 *   「みなし残業 0 時間」なので 0:00 を置く。一方の「固定残業時間の超過分」は
 *   超過という概念が無いため「—」にしてあり、両列で表記が揃わないのは意図的。
 * - 固定残業時間の超過分 = 時間外労働時間（R038）− 契約テーブルの固定残業時間（負なら 0:00）。
 * - 割増単価算出時間（R030）は当月の実績ではなく就業規則が定める月平均所定労働時間
 *   （週所定労働時間 × 52 ÷ 12）。R022 とは一致しないのが正常で、この 2 列が別項目として
 *   カタログにあるのはそのため。
 * - 欠勤控除算出日数（R031）も同様に月平均所定労働日数（週所定労働日数 × 52 ÷ 12）で、
 *   当月所定労働日数（R039）とは一致しない。
 * - 管理監督者（契約テーブルで該当の EMP-1005）は時間外・休日の割増対象外なので
 *   R023 / R038 とも 0:00。深夜割増は必要なので深夜労働時間（R027）は集計し、
 *   時間外が 0:00 である以上その内訳は法定内深夜（R035）に置く。
 */
export const payrollReferenceAttendanceTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '氏名',
    sourcePath: '勤怠 › 従業員 › 氏名'
  }, {
    label: '社員番号',
    sourcePath: '勤怠 › 従業員 › 従業員番号'
  },
  // R021
  {
    align: 'end',
    label: '欠勤日数（日）',
    payrollItemId: 'pi-r021',
    sourcePath: '勤怠 › 月次集計 › 欠勤日数'
  },
  // R022
  {
    align: 'end',
    label: '当月所定労働時間（時間:分）',
    payrollItemId: 'pi-r022',
    sourcePath: '勤怠 › 月次集計 › 当月所定労働時間'
  },
  // R023（id は `pi-r023` ではなく `pi-overtime-hours`）
  {
    align: 'end',
    label: '残業時間（時間:分）',
    payrollItemId: 'pi-overtime-hours',
    sourcePath: '勤怠 › 月次集計 › 残業時間'
  },
  // R024
  {
    align: 'end',
    label: 'みなし残業時間（時間:分）',
    payrollItemId: 'pi-r024',
    sourcePath: '勤怠 › 月次集計 › みなし残業時間'
  },
  // R025
  {
    align: 'end',
    label: '出勤日数（日）',
    payrollItemId: 'pi-r025',
    sourcePath: '勤怠 › 月次集計 › 出勤日数'
  },
  // R026
  {
    align: 'end',
    label: '有給取得日数（日）',
    payrollItemId: 'pi-r026',
    sourcePath: '勤怠 › 休暇管理 › 年次有給休暇取得日数'
  },
  // R027
  {
    align: 'end',
    label: '深夜労働時間（時間:分）',
    payrollItemId: 'pi-r027',
    sourcePath: '勤怠 › 月次集計 › 深夜労働時間'
  },
  // R028
  {
    align: 'end',
    label: '休日労働時間（時間:分）',
    payrollItemId: 'pi-r028',
    sourcePath: '勤怠 › 月次集計 › 休日労働時間'
  },
  // R029
  {
    align: 'end',
    label: '遅刻早退時間（時間:分）',
    payrollItemId: 'pi-r029',
    sourcePath: '勤怠 › 月次集計 › 遅刻早退時間'
  },
  // R030
  {
    align: 'end',
    label: '割増単価算出時間（時間:分）',
    payrollItemId: 'pi-r030',
    sourcePath: '勤怠 › 月次集計 › 割増単価算出時間'
  },
  // R031
  {
    align: 'end',
    label: '欠勤控除算出日数（日）',
    payrollItemId: 'pi-r031',
    sourcePath: '勤怠 › 月次集計 › 欠勤控除算出日数'
  },
  // R032
  {
    align: 'end',
    label: '時間外労働時間（法定外・45h以内）（時間:分）',
    payrollItemId: 'pi-r032',
    sourcePath: '勤怠 › 月次集計 › 時間外労働時間（45時間以内）'
  },
  // R033
  {
    align: 'end',
    label: '時間外労働時間（45h超）（時間:分）',
    payrollItemId: 'pi-r033',
    sourcePath: '勤怠 › 月次集計 › 時間外労働時間（45時間超）'
  },
  // R034
  {
    align: 'end',
    label: '時間外労働時間（60h超）（時間:分）',
    payrollItemId: 'pi-r034',
    sourcePath: '勤怠 › 月次集計 › 時間外労働時間（60時間超）'
  },
  // R035
  {
    align: 'end',
    label: '法定内深夜時間（時間:分）',
    payrollItemId: 'pi-r035',
    sourcePath: '勤怠 › 月次集計 › 深夜労働時間（法定内）'
  },
  // R036
  {
    align: 'end',
    label: '法定外深夜時間（時間:分）',
    payrollItemId: 'pi-r036',
    sourcePath: '勤怠 › 月次集計 › 深夜労働時間（法定外）'
  },
  // R037
  {
    align: 'end',
    label: '法定休日深夜時間（時間:分）',
    payrollItemId: 'pi-r037',
    sourcePath: '勤怠 › 月次集計 › 深夜労働時間（法定休日）'
  },
  // R038
  {
    align: 'end',
    label: '時間外労働時間（制度反映）（時間:分）',
    payrollItemId: 'pi-r038',
    sourcePath: '勤怠 › 月次集計 › 時間外労働時間'
  },
  // R039
  {
    align: 'end',
    label: '当月所定労働日数（日）',
    payrollItemId: 'pi-r039',
    sourcePath: '勤怠 › 月次集計 › 所定労働日数'
  },
  // R050
  {
    align: 'end',
    label: '労働時間数（時間:分）',
    payrollItemId: 'pi-r050',
    sourcePath: '勤怠 › 月次集計 › 労働時間数'
  },
  // ここから下は payroll item カタログに無いが給与計算に要る細目
  // （入力スナップショットと割増計算の内訳由来）。
  {
    label: '集計対象期間',
    sourcePath: '勤怠 › 月次集計 › 集計対象期間'
  }, {
    align: 'end',
    label: '所定内労働時間（時間:分）',
    sourcePath: '勤怠 › 月次集計 › 所定内労働時間'
  }, {
    align: 'end',
    label: '法定休日労働時間（時間:分）',
    sourcePath: '勤怠 › 月次集計 › 法定休日労働時間'
  }, {
    align: 'end',
    label: '法定外休日労働時間（時間:分）',
    sourcePath: '勤怠 › 月次集計 › 法定外休日労働時間'
  }, {
    align: 'end',
    label: '休日出勤日数（日）',
    sourcePath: '勤怠 › 月次集計 › 休日出勤日数'
  }, {
    align: 'end',
    label: '振替休日取得日数（日）',
    sourcePath: '勤怠 › 休暇管理 › 振替休日取得日数'
  }, {
    align: 'end',
    label: '代休取得日数（日）',
    sourcePath: '勤怠 › 休暇管理 › 代休取得日数'
  }, {
    align: 'end',
    label: '特別休暇取得日数（日）',
    sourcePath: '勤怠 › 休暇管理 › 特別休暇取得日数'
  }, {
    align: 'end',
    label: '固定残業時間の超過分（時間:分）',
    sourcePath: '勤怠 › 月次集計 › 時間外労働時間（固定残業超過分）'
  }],
  rows: [['田村 拓真', 'EMP-1001', '0', '176:00', '32:00', '30:00', '22', '0', '4:00', '8:00', '0:00', '173:20', '21', '32:00', '0:00', '0:00', '0:00', '4:00', '0:00', '32:00', '22', '208:00', '2026年7月度', '176:00', '0:00', '8:00', '1', '0', '1', '0', '2:00'], ['岡部 美咲', 'EMP-1002', '0', '132:00', '0:00', '0:00', '20', '2', '0:00', '0:00', '1:30', '130:00', '21', '0:00', '0:00', '0:00', '0:00', '0:00', '0:00', '0:00', '22', '118:30', '2026年7月度', '118:30', '0:00', '0:00', '0', '0', '0', '0', '—'], ['川島 隆之', 'EMP-1003', '0', '176:00', '18:00', '0:00', '20', '1', '0:00', '0:00', '0:00', '173:20', '21', '18:00', '0:00', '0:00', '0:00', '0:00', '0:00', '18:00', '22', '178:00', '2026年7月度', '160:00', '0:00', '0:00', '0', '0', '0', '1', '—'], ['大久保 稔', 'EMP-1004', '0', '80:00', '0:00', '0:00', '16', '0', '0:00', '0:00', '0:00', '86:40', '17', '0:00', '0:00', '0:00', '0:00', '0:00', '0:00', '0:00', '16', '80:00', '2026年7月度', '80:00', '0:00', '0:00', '0', '0', '0', '0', '—'], ['藤原 里美', 'EMP-1005', '0', '176:00', '0:00', '0:00', '22', '0', '6:00', '0:00', '0:00', '173:20', '21', '0:00', '0:00', '0:00', '6:00', '0:00', '0:00', '0:00', '22', '176:00', '2026年7月度', '176:00', '0:00', '0:00', '0', '0', '0', '0', '—'], ['小柳 亮介', 'EMP-1006', '0', '176:00', '72:00', '20:00', '22', '0', '12:00', '24:00', '0:00', '173:20', '21', '45:00', '15:00', '6:00', '0:00', '8:00', '4:00', '66:00', '22', '250:00', '2026年7月度', '176:00', '8:00', '16:00', '3', '1', '0', '0', '46:00'], ['中原 千夏', 'EMP-1007', '1', '176:00', '4:00', '0:00', '20', '1', '0:00', '0:00', '2:00', '173:20', '21', '4:00', '0:00', '0:00', '0:00', '0:00', '0:00', '4:00', '22', '162:00', '2026年7月度', '158:00', '0:00', '0:00', '0', '0', '0', '0', '—'], ['望月 康平', 'EMP-1008', '0', '176:00', '28:00', '0:00', '22', '0', '0:00', '8:00', '0:00', '173:20', '21', '24:00', '0:00', '0:00', '0:00', '0:00', '0:00', '24:00', '22', '208:00', '2026年7月度', '176:00', '8:00', '0:00', '1', '0', '1', '0', '—'], ['石垣 芽衣', 'EMP-1009', '3', '176:00', '6:00', '0:00', '19', '0', '0:00', '0:00', '0:30', '173:20', '21', '6:00', '0:00', '0:00', '0:00', '0:00', '0:00', '6:00', '22', '157:30', '2026年7月度', '151:30', '0:00', '0:00', '0', '0', '0', '0', '—'], ['篠原 正和', 'EMP-1010', '0', '176:00', '28:00', '20:00', '22', '0', '0:00', '8:00', '0:00', '173:20', '21', '28:00', '0:00', '0:00', '0:00', '0:00', '0:00', '28:00', '22', '204:00', '2026年7月度', '176:00', '0:00', '8:00', '1', '1', '0', '0', '8:00']]
};